import { useEffect, type RefObject } from 'react';

const IGNORE_SELECTOR = 'input, textarea, select, [contenteditable="true"], .responsive-modal-backdrop, .phone-cal-sheet';

/**
 * Calls onPrev/onNext for a quick, mostly horizontal swipe on the element.
 * Swiping left goes forward (next), swiping right goes back (previous).
 */
export function useSwipeNavigation(
  ref: RefObject<HTMLElement | null>,
  { onPrev, onNext, enabled = true }: { onPrev: () => void; onNext: () => void; enabled?: boolean }
) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    let start: { x: number; y: number; time: number } | null = null;

    const handleStart = (event: TouchEvent) => {
      const target = event.target as Element | null;
      if (event.touches.length !== 1 || target?.closest(IGNORE_SELECTOR)) {
        start = null;
        return;
      }
      const touch = event.touches[0];
      start = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    };

    const handleEnd = (event: TouchEvent) => {
      if (!start) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      const elapsed = Date.now() - start.time;
      start = null;
      if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5 || elapsed > 600) return;
      if (dx < 0) onNext();
      else onPrev();
    };

    const handleCancel = () => { start = null; };

    el.addEventListener('touchstart', handleStart, { passive: true });
    el.addEventListener('touchend', handleEnd, { passive: true });
    el.addEventListener('touchcancel', handleCancel, { passive: true });
    return () => {
      el.removeEventListener('touchstart', handleStart);
      el.removeEventListener('touchend', handleEnd);
      el.removeEventListener('touchcancel', handleCancel);
    };
  }, [ref, onPrev, onNext, enabled]);
}
